import { useField } from '@kizi/comparator-core';
import { Div, P } from '@kizi/core-components';
import { IconV2 as Icon } from '@kizi/shared-components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { WithFormProps } from './HOCs';
import {
  BasicInput,
  NoSuccessIconBasicInput,
} from './wrappers/inputs/withFormProps';
export interface SearchOption {
  id: number;
  label: string;
}

export interface SearchProps
  extends WithFormProps<
    React.ComponentPropsWithRef<typeof NoSuccessIconBasicInput>
  > {
  options?: SearchOption[];
  selectedOptions?: SearchOption[];
  value: any;
  onChange: (val: any) => void;
  placeholder?: string;
  showSelectedValues?: boolean;
}

export const Search: React.FC<SearchProps> = ({
  options = [],
  value = [],
  onChange,
  placeholder,
  showSelectedValues = true,
  ...restProps
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [isOptionsShow, setIsOptionsShown] = useState<boolean>(false);
  const [optionsToShow, updateOptionsToShow] = useState<SearchOption[]>([]);
  const [searchedValue, setSearchedValue] = useState('');
  const [selectedValues, updateSelectedValues] =
    useState<SearchOption[]>(value);

  function handleClickOutside(event: MouseEvent) {
    if (
      wrapperRef.current &&
      !wrapperRef.current.contains(event.target as Node)
    ) {
      setIsOptionsShown(false);
    }
  }

  function deleteSelectedValue(idToDelete: number) {
    updateSelectedValues(selectedValues?.filter((v) => v.id !== idToDelete));
  }

  useEffect(() => {
    updateOptionsToShow(options);
  }, [options]);

  useEffect(() => {
    updateSelectedValues(value);
  }, [value]);

  useEffect(() => {
    if (searchedValue) {
      updateOptionsToShow(
        options.filter((el) =>
          el.label.toLowerCase().includes(searchedValue.toLowerCase()),
        ),
      );
    } else {
      updateOptionsToShow(
        options.filter((el) => {
          return selectedValues.length > 0
            ? !selectedValues.includes(el)
            : undefined;
        }),
      );
    }
  }, [searchedValue]);

  useEffect(() => {
    if (onChange && selectedValues.length > 0) {
      onChange(selectedValues);
      setSearchedValue('');
    }
  }, [selectedValues]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Div data-testid="search-container">
      <Div
        css={{ position: 'relative' }}
        data-testid="search-input-wrapper"
        ref={wrapperRef}
      >
        <BasicInput
          name="search"
          value={searchedValue}
          onFocus={() => setIsOptionsShown(true)}
          onChange={(e) => {
            setSearchedValue(e.target.value);
          }}
          placeholder={placeholder || 'Zoek een zender...'}
          pr={40}
          autoComplete="off"
          {...restProps}
        />
        {isOptionsShow && optionsToShow?.length > 0 && (
          <Div variant="searchModal" data-testid="search-option-variants">
            {optionsToShow.map((el) => {
              if (selectedValues.findIndex((v) => v.id === el.id) > -1) {
                return;
              }
              return (
                <P
                  key={el.id}
                  variant="searchOption"
                  onClick={() => {
                    updateSelectedValues([...selectedValues, el]);
                    setIsOptionsShown(false);
                  }}
                >
                  {el.label}
                </P>
              );
            })}
          </Div>
        )}
      </Div>
      {showSelectedValues && selectedValues?.length > 0 && (
        <Div data-testid="selected-search-option">
          {selectedValues.map((v) => {
            return (
              <Div
                display="flex"
                flexDirection="row"
                alignItems="center"
                key={v.id}
                mb={2}
              >
                <P marginRight={1} variant="bodyRegular">
                  {v.label}
                </P>
                <Icon
                  data-testid="close-icon"
                  name="cancel"
                  fill="second.0"
                  height={12}
                  width={12}
                  css={{ cursor: 'pointer' }}
                  onClick={() => deleteSelectedValue(v.id)}
                />
              </Div>
            );
          })}
        </Div>
      )}
    </Div>
  );
};

export interface SearchWrapperProps
  extends Omit<SearchProps, 'value' | 'onChange'> {
  _fieldName: string;
  id?: string;
}

export const SearchWrapper: React.FC<SearchWrapperProps> = ({
  _fieldName,
  id,
  ...props
}) => {
  const field = useField(_fieldName);

  if (!field) return null;

  const parsedValues = useMemo(
    () =>
      field?.value !== undefined ? JSON.parse(field?.value as string) : [],
    [field?.value],
  );

  const onChange = (val) => {
    const value = id
      ? JSON.stringify({ ...parsedValues, [id]: val })
      : JSON.stringify(val);
    field?.set({ value: value });
  };

  return (
    <Search
      {...props}
      onChange={onChange}
      value={id ? parsedValues[id] : parsedValues}
    />
  );
};
